import {useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Tag} from '@/components/ui/tag'
import {Label} from '@/components/ui/label'
import {CatalogOtherField} from '@/components/catalog-other-field'
import {DateField} from '@/components/date-field'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {FieldError} from '@/components/field-error'
import {HelpIcon} from '@/components/help-icon'
import {Hint} from '@/components/ui/hint'
import {PorQue} from '@/components/ui/por-que'
import {TagCombobox} from '@/components/tag-combobox'
import {api} from '@/lib/api'
import {track} from '@/lib/telemetry'
import {translateApiError} from '@/lib/errors'
import {useUpgradeGate} from '@/components/billing/use-upgrade-gate'
import {ConfirmarAltaDialog} from '@/components/confirmar-identidad-dialog'
import {avisarSiQuedaEnVerificacion, pideConfirmarIdentidad} from '@/hooks/platform/use-access'
import {TipoDocumentoSelect} from '@/components/tipo-documento-select'
import {DOCUMENTO_MAX, documentoCorto, type TipoDocumento} from '@/lib/documento'
import {validateCedula, validateEmail, validateNombre, validatePhone} from '@/lib/validation'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import {useDebouncedValue} from '@/hooks/common/use-debounced-value'
import {useEmployeeLicencia} from '@/hooks/inci/consola/use-licencia'
import {useTags} from '@/hooks/empresa/organizacion/use-tags'
import {useGodMode} from '@/hooks/platform/use-god-mode'
import {cn} from '@/lib/utils'
import {tope} from '@/lib/campos-texto'

export const EMPLOYMENT_TYPES = ['empleado', 'contratista', 'aprendiz', 'temporal'] as const

// Espejo de CONTRACT_TYPES en backend/app/common/models/validation.py; '' = sin definir.
const CONTRACT_TYPES = ['indefinido', 'fijo', 'obra_labor', 'aprendizaje_sena', 'prestacion_servicios', 'otro'] as const

type FormState = {
  cedula: string
  tipo_documento: TipoDocumento
  name: string
  last_name: string
  email: string
  phone: string
  role: Employee['role']
  status: 'active' | 'inactive'
  employment_type: (typeof EMPLOYMENT_TYPES)[number]
  contract_type: '' | (typeof CONTRACT_TYPES)[number]
  positions: string[]
  areas: string[]
  sede: string
  // El plazo del examen por cambio de ocupación corre desde este día, no desde el del tecleo;
  // solo viaja cuando la edición cambia un cargo por otro.
  fecha_cambio_cargo: string
}

type FieldErrors = Partial<Record<keyof FormState, string>>

function toForm(employee?: Employee): FormState {
  return {
    cedula: employee?.cedula ?? '',
    tipo_documento: employee?.tipo_documento ?? 'CC',
    name: employee?.name ?? '',
    last_name: employee?.last_name ?? '',
    email: employee?.email ?? '',
    phone: employee?.phone ?? '',
    role: employee?.role ?? 'user',
    status: employee?.status ?? 'active',
    employment_type: employee?.employment_type ?? 'empleado',
    contract_type: (employee?.contract_type as FormState['contract_type']) ?? '',
    positions: employee?.positions.map((p) => p.name) ?? [],
    areas: employee?.areas.map((a) => a.name) ?? [],
    sede: employee?.sede?.name ?? '',
    fecha_cambio_cargo: '',
  }
}

/** Espejo de `cambio_de_cargo` del backend: con más de un cargo que entra o sale, cuál
 * reemplaza a cuál lo decide una persona, así que no hay cambio que fechar. */
function cambiaDeCargo(antes: string[], despues: string[]): boolean {
  const salieron = antes.filter((cargo) => !despues.includes(cargo))
  const entraron = despues.filter((cargo) => !antes.includes(cargo))
  return salieron.length === 1 && entraron.length === 1
}

/** El empleador escribe la identidad solo al dar de alta o al corregir la que él declaró;
 * fuera de eso es de la persona y solo ella la edita. */
function editsIdentity(employee?: Employee): boolean {
  return !employee || employee.identidad_declarada_por_empresa
}

function validateForm(form: FormState, employee?: Employee, corrigeCedula = false): FieldErrors {
  const errors: FieldErrors = {}
  if (corrigeCedula) {
    const cedula = validateCedula(form.cedula, form.tipo_documento)
    if (cedula) errors.cedula = cedula
  }
  if (!editsIdentity(employee)) return errors
  if (!employee) {
    const cedula = validateCedula(form.cedula, form.tipo_documento)
    if (cedula) errors.cedula = cedula
  }
  const nombre = form.name ? validateNombre(form.name) : null
  if (nombre) errors.name = nombre
  const apellido = form.last_name ? validateNombre(form.last_name) : null
  if (apellido) errors.last_name = apellido
  const email = form.email ? validateEmail(form.email) : null
  if (email) errors.email = email
  const phone = form.phone ? validatePhone(form.phone) : null
  if (phone) errors.phone = phone
  return errors
}

function IdentityFields({
  form,
  set,
  blur,
  fieldErrors,
}: {
  form: FormState
  set: (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => void
  blur: (field: keyof FieldErrors, validate: (raw: string) => string | null) => void
  fieldErrors: FieldErrors
}) {
  const {t} = useTranslation()
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="name">{t('employees.fields.name')}</Label>
        <Input
          id="name"
          value={form.name}
          onChange={set('name')}
          onBlur={() => blur('name', (raw) => (raw ? validateNombre(raw) : null))}
          required
          maxLength={tope('NOM')}
          aria-invalid={!!fieldErrors.name}
        />
        <FieldError error={fieldErrors.name} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="last_name">{t('employees.fields.lastName')}</Label>
        <Input
          id="last_name"
          value={form.last_name}
          onChange={set('last_name')}
          onBlur={() => blur('last_name', (raw) => (raw ? validateNombre(raw) : null))}
          required
          maxLength={tope('NOM')}
          aria-invalid={!!fieldErrors.last_name}
        />
        <FieldError error={fieldErrors.last_name} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="email">{t('employees.fields.email')}</Label>
        <Input
          id="email"
          inputMode="email"
          autoComplete="email"
          value={form.email}
          onChange={set('email')}
          onBlur={() => blur('email', (raw) => (raw ? validateEmail(raw) : null))}
          maxLength={tope('MAIL')}
          aria-invalid={!!fieldErrors.email}
        />
        <FieldError error={fieldErrors.email} />
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone">{t('employees.fields.phone')}</Label>
        <Input
          id="phone"
          inputMode="tel"
          value={form.phone}
          onChange={set('phone')}
          onBlur={() => blur('phone', (raw) => (raw ? validatePhone(raw) : null))}
          maxLength={tope('TEL')}
          aria-invalid={!!fieldErrors.phone}
        />
        <FieldError error={fieldErrors.phone} />
      </div>
    </>
  )
}

function DocumentoInputs({
  form,
  set,
  blur,
  fieldErrors,
  setTipo,
  testId,
}: {
  form: FormState
  set: (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => void
  blur: (field: keyof FieldErrors, validate: (raw: string) => string | null) => void
  fieldErrors: FieldErrors
  setTipo: (tipo: TipoDocumento) => void
  testId?: string
}) {
  return (
    <div className="flex gap-2">
      <TipoDocumentoSelect
        value={form.tipo_documento}
        onChange={setTipo}
        testId={testId ? `${testId}-tipo` : 'cedula-tipo'}
      />
      <Input
        id="cedula"
        className="flex-1"
        value={form.cedula}
        onChange={set('cedula')}
        onBlur={() => blur('cedula', (raw) => validateCedula(raw, form.tipo_documento))}
        required
        maxLength={DOCUMENTO_MAX[form.tipo_documento]}
        inputMode={form.tipo_documento === 'PA' ? 'text' : 'numeric'}
        aria-invalid={!!fieldErrors.cedula}
        data-testid={testId}
      />
    </div>
  )
}

function CedulaField({
  employee,
  corrige,
  form,
  set,
  blur,
  fieldErrors,
  setTipo,
}: {
  employee: Employee
  corrige: boolean
  form: FormState
  set: (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => void
  blur: (field: keyof FieldErrors, validate: (raw: string) => string | null) => void
  fieldErrors: FieldErrors
  setTipo: (tipo: TipoDocumento) => void
}) {
  const {t} = useTranslation()
  if (!corrige) {
    return (
      <ReadonlyStamp
        label={t('documento.label')}
        value={<span className="font-mono">{documentoCorto(employee.tipo_documento, employee.cedula)}</span>}
      />
    )
  }
  return (
    <div className="space-y-2">
      <Label htmlFor="cedula">{t('documento.label')}</Label>
      <DocumentoInputs
        form={form}
        set={set}
        blur={blur}
        fieldErrors={fieldErrors}
        setTipo={setTipo}
        testId="cedula-admin-input"
      />
      <FieldError error={fieldErrors.cedula} />
      <Hint testId="cedula-admin-hint">{t('employees.cedulaAdminHint')}</Hint>
    </div>
  )
}

function ReadonlyStamp({label, value, className}: {label: string; value: ReactNode; className?: string}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">{label}</p>
      <div className="text-sm text-foreground">{value}</div>
    </div>
  )
}

/** La empresa ve solo categoría y estado, nunca el documento: la licencia la edita solo la persona. */
function LicenciaStatusStamp({businessId, employeeId}: {businessId: string; employeeId: string}) {
  const {t} = useTranslation()
  const {data} = useEmployeeLicencia(businessId, employeeId)
  return (
    <div
      className="space-y-1.5 rounded-lg border border-border bg-secondary/40 p-3"
      data-testid="employee-licencia-stamp">
      <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
        {t('licencia.sectionTitle')}
      </p>
      {!data?.estado ? (
        <p className="text-xs text-muted-foreground">{t('licencia.notUploadedYet')}</p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {data.categoria && <Tag tone="neutral">{t(`licencia.categorias.${data.categoria}`)}</Tag>}
          <Tag tone={data.estado === 'verificada' ? 'green' : data.estado === 'rechazada' ? 'red' : 'amber'}>
            {t(`licencia.estado.${data.estado}`)}
          </Tag>
        </div>
      )}
    </div>
  )
}

export function EmployeeFormDialog({
  businessId,
  employee,
  trigger,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  canAssignRole = true,
}: {
  businessId: string
  employee?: Employee
  /** Se omite cuando el trigger vive en algo que se desmonta al clic (un Popover): el llamador
   * controla `open`. */
  trigger?: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** El rol es frontera de privilegio: solo el dueño lo reasigna (el backend responde 403). */
  canAssignRole?: boolean
}) {
  const {t} = useTranslation()
  const queryClient = useQueryClient()
  const isEdit = !!employee
  // La cédula es la identidad de login: solo el modo dios con la edición armada la corrige.
  const god = useGodMode()
  const corrigeCedula = isEdit && god.active && god.armed
  const cargosOriginales = employee?.positions.map((p) => p.name) ?? []
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const open = controlledOpen ?? uncontrolledOpen
  const setOpen = setControlledOpen ?? setUncontrolledOpen
  const [form, setForm] = useState<FormState>(() => toForm(employee))
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  // El `PorQue` se monta dentro del diálogo; si no, Escape cierra la ficha en vez del popover.
  const [nodo, setNodo] = useState<HTMLElement | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmando, setConfirmando] = useState(false)
  const upgradeGate = useUpgradeGate(businessId, 'empleado', (band) =>
    setError(t('planFacturacion.upgradeModal.retryNote', {band}))
  )
  // Solo con el diálogo abierto: cada fila del listado monta su propio diálogo de edición.
  const [cargoQuery, setCargoQuery] = useState('')
  const [areaQuery, setAreaQuery] = useState('')
  const {data: positions} = useTags(businessId, 'positions', open, useDebouncedValue(cargoQuery, 300))
  const {data: areas} = useTags(businessId, 'areas', open, useDebouncedValue(areaQuery, 300))
  const {data: sedes} = useTags(businessId, 'sedes', open)

  function handleOpenChange(next: boolean) {
    setOpen(next)
    setError(null)
    setFieldErrors({})
    if (next) setForm(toForm(employee))
  }

  function set(field: keyof FormState) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((f) => ({...f, [field]: e.target.value}))
      setFieldErrors((fe) => ({...fe, [field]: undefined}))
    }
  }

  function setTipo(tipo: TipoDocumento) {
    setForm((f) => ({...f, tipo_documento: tipo}))
    setFieldErrors((fe) => ({...fe, cedula: undefined}))
  }

  function blur(field: keyof FieldErrors, validate: (raw: string) => string | null) {
    setFieldErrors((fe) => ({...fe, [field]: validate(form[field] as string) ?? undefined}))
  }

  const mutation = useMutation({
    mutationFn: async (confirmar_identidad: boolean) => {
      if (isEdit) {
        // El nombre y el contacto son de la persona; la empresa solo corrige la identidad que
        // declaró ella misma (409 si no la hay).
        const identidad = editsIdentity(employee)
          ? {name: form.name, last_name: form.last_name, email: form.email || '', phone: form.phone || ''}
          : {}
        // Tipo y número viajan juntos: el backend los valida en conjunto.
        const cambioDocumento = form.cedula !== employee.cedula || form.tipo_documento !== employee.tipo_documento
        const cedula =
          corrigeCedula && cambioDocumento ? {cedula: form.cedula, tipo_documento: form.tipo_documento} : {}
        // Mandar el rol sin poder asignarlo vale un 403, aunque no cambie.
        const rol = canAssignRole ? {role: form.role} : {}
        const desdeCuando = cambiaDeCargo(cargosOriginales, form.positions)
          ? {fecha_cambio_cargo: form.fecha_cambio_cargo || null}
          : {}
        return (
          await api.patch(`/businesses/${businessId}/employees/${employee.id}`, {
            ...rol,
            ...desdeCuando,
            status: form.status,
            employment_type: form.employment_type,
            contract_type: form.contract_type || null,
            positions: form.positions,
            areas: form.areas,
            sede: form.sede,
            ...identidad,
            ...cedula,
          })
        ).data
      }
      return (
        await api.post(`/businesses/${businessId}/employees`, {
          cedula: form.cedula,
          tipo_documento: form.tipo_documento,
          name: form.name,
          last_name: form.last_name,
          email: form.email || null,
          phone: form.phone || null,
          role: form.role,
          employment_type: form.employment_type,
          contract_type: form.contract_type || null,
          positions: form.positions,
          areas: form.areas,
          confirmar_identidad,
        })
      ).data
    },
    onSuccess: (guardado: Employee) => {
      setConfirmando(false)
      queryClient.invalidateQueries({queryKey: ['employees', businessId]})
      queryClient.invalidateQueries({queryKey: ['sgsst', businessId]})
      // Un cargo, área o sede tecleado puede ser una etiqueta nueva (find-or-create).
      queryClient.invalidateQueries({queryKey: ['tags', businessId]})
      if (!isEdit) track('employees_registered', {business_id: businessId})
      if (!isEdit) avisarSiQuedaEnVerificacion(guardado, t)
      handleOpenChange(false)
    },
    onError: (err) => {
      setConfirmando(false)
      if (!isEdit && pideConfirmarIdentidad(err)) {
        setConfirmando(true)
        return
      }
      // Un alta que cruza la banda paga responde 409 antes de crear la fila (upgrade-gating-spec §1b).
      if (upgradeGate.catchGate(err, {proposed_active_delta: 1})) return
      setError(translateApiError(err))
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const errors = validateForm(form, employee, corrigeCedula)
    setFieldErrors(errors)
    if (Object.values(errors).some(Boolean)) return
    mutation.mutate(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent ref={setNodo} className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? t('employees.edit.title') : t('employees.create.title')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          {isEdit && employee.identidad_declarada_por_empresa ? (
            <div className="space-y-3 rounded-lg border border-border bg-secondary/40 p-3">
              <div className="flex flex-col gap-3">
                <CedulaField
                  employee={employee}
                  corrige={corrigeCedula}
                  form={form}
                  set={set}
                  blur={blur}
                  fieldErrors={fieldErrors}
                  setTipo={setTipo}
                />
                <IdentityFields form={form} set={set} blur={blur} fieldErrors={fieldErrors} />
              </div>
              <PorQue
                label={t('employees.identidadPorqueLabel')}
                testId="identidad-declarada-porque"
                contenidoTestId="identidad-declarada-note"
                contenedor={nodo}>
                {t('employees.identidadDeclaradaNote')}
              </PorQue>
            </div>
          ) : null}

          {isEdit && !employee.identidad_declarada_por_empresa ? (
            <div className="space-y-3 rounded-lg border border-border bg-secondary/40 p-3">
              <div className="flex flex-col gap-3">
                <ReadonlyStamp label={t('employees.fields.name')} value={`${employee.name} ${employee.last_name}`} />
                <CedulaField
                  employee={employee}
                  corrige={corrigeCedula}
                  form={form}
                  set={set}
                  blur={blur}
                  fieldErrors={fieldErrors}
                  setTipo={setTipo}
                />
                <ReadonlyStamp
                  label={t('employees.fields.email')}
                  value={employee.email || <span className="text-muted-foreground">—</span>}
                />
                <ReadonlyStamp
                  label={t('employees.fields.phone')}
                  value={employee.phone || <span className="text-muted-foreground">—</span>}
                />
              </div>
              <Hint testId="identidad-gestionada-hint">{t('employees.identityManagedHint')}</Hint>
              <PorQue
                label={t('employees.identityPorqueLabel')}
                testId="identidad-gestionada-porque"
                contenidoTestId="identidad-gestionada-note"
                contenedor={nodo}>
                {t('employees.identityManagedNote', {name: employee.name})}
              </PorQue>
            </div>
          ) : null}

          {isEdit && form.role === 'responsable' && (
            <LicenciaStatusStamp businessId={businessId} employeeId={employee.id} />
          )}

          {!isEdit && (
            <>
              <div className="flex flex-col gap-3">
                <div className="space-y-2">
                  <Label htmlFor="cedula">{t('documento.label')}</Label>
                  <DocumentoInputs form={form} set={set} blur={blur} fieldErrors={fieldErrors} setTipo={setTipo} />
                  {/* El formato queda visible en reposo: el placeholder se borra al teclear. */}
                  <Hint>{t(form.tipo_documento === 'PA' ? 'documento.hintPA' : 'documento.hintCC')}</Hint>
                  <FieldError error={fieldErrors.cedula} />
                </div>
                <IdentityFields form={form} set={set} blur={blur} fieldErrors={fieldErrors} />
              </div>
              <Hint testId="create-no-access-hint">{t('employees.create.noAccessHint')}</Hint>
            </>
          )}

          <div className="flex flex-col gap-3">
            <div className="space-y-2">
              <span className="flex items-center gap-1.5">
                <Label htmlFor="role">{t('employees.fields.role')}</Label>
                <HelpIcon
                  label={t('common.help.iconLabelFor', {campo: t('employees.fields.role')})}
                  testId="employee-role-help">
                  {t('employees.fields.roleHelp')}
                </HelpIcon>
              </span>
              {canAssignRole ? (
                <select
                  id="role"
                  value={form.role}
                  onChange={(e) => setForm((f) => ({...f, role: e.target.value as Employee['role']}))}
                  className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
                  <option value="user">{t('roles.user')}</option>
                  <option value="owner">{t('roles.owner')}</option>
                  <option value="responsable">{t('roles.responsable')}</option>
                </select>
              ) : (
                <div className="flex h-9 items-center" data-testid="employee-role-readonly">
                  <Tag tone="neutral">{t(`roles.${form.role}`)}</Tag>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="employment_type">{t('employees.fields.employmentType')}</Label>
              <select
                id="employment_type"
                value={form.employment_type}
                onChange={(e) =>
                  setForm((f) => ({...f, employment_type: e.target.value as FormState['employment_type']}))
                }
                className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
                {EMPLOYMENT_TYPES.map((employmentType) => (
                  <option key={employmentType} value={employmentType}>
                    {t(`employees.employmentType.${employmentType}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contract_type">{t('employees.fields.contractType')}</Label>
              <select
                id="contract_type"
                value={form.contract_type}
                onChange={(e) => setForm((f) => ({...f, contract_type: e.target.value as FormState['contract_type']}))}
                className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
                <option value="">{t('employees.contractType.unspecified')}</option>
                {CONTRACT_TYPES.map((contractType) => (
                  <option key={contractType} value={contractType}>
                    {t(`employees.contractType.${contractType}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <TagCombobox
              label={t('tags.kind.positions')}
              testId="cargo"
              available={positions?.items ?? []}
              value={form.positions}
              onChange={(names) => setForm((f) => ({...f, positions: names}))}
              onQueryChange={setCargoQuery}
            />
            {isEdit && cambiaDeCargo(cargosOriginales, form.positions) && (
              <DateField
                id="employee-fecha-cambio-cargo"
                label={t('cambioOcupacion.fechaLabel')}
                hint={t('cambioOcupacion.fechaHint')}
                value={form.fecha_cambio_cargo}
                onChange={(value) => setForm((f) => ({...f, fecha_cambio_cargo: value}))}
              />
            )}
            <TagCombobox
              label={t('tags.kind.areas')}
              testId="area"
              available={areas?.items ?? []}
              value={form.areas}
              onChange={(names) => setForm((f) => ({...f, areas: names}))}
              onQueryChange={setAreaQuery}
            />
          </div>

          {isEdit && (
            <CatalogOtherField
              id="employee-sede"
              label={t('ficha.sede')}
              help={t('employees.fields.sedeHelp')}
              options={(sedes?.items ?? []).map((sede) => sede.name)}
              value={form.sede}
              onChange={(value) => setForm((f) => ({...f, sede: value}))}
              onCommit={(value) => setForm((f) => ({...f, sede: value}))}
            />
          )}

          {isEdit && (
            <div className="space-y-2">
              <Label>{t('employees.fields.status')}</Label>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-input px-3 py-2">
                <div className="flex min-w-0 items-center gap-2">
                  <Tag tone="neutral">{t(`employees.status.${form.status}`)}</Tag>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setForm((f) => ({...f, status: f.status === 'active' ? 'inactive' : 'active'}))}>
                  {t(form.status === 'active' ? 'employees.deactivate' : 'employees.activate')}
                </Button>
              </div>
            </div>
          )}

          <FormDialogFooter
            error={error}
            pending={mutation.isPending}
            submitLabel={isEdit ? t('employees.edit.submit') : t('employees.create.submit')}
            onCancel={() => handleOpenChange(false)}
          />
        </form>
      </DialogContent>
      {upgradeGate.modal}
      <ConfirmarAltaDialog
        open={confirmando}
        onOpenChange={setConfirmando}
        nombre={`${form.name} ${form.last_name}`}
        cedula={form.cedula}
        onConfirm={() => mutation.mutate(true)}
        pending={mutation.isPending}
      />
    </Dialog>
  )
}
